import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useDispatch, useSelector} from 'react-redux'
import {useEffect, useMemo} from 'react'
import {useMemoizedFn} from 'ahooks'
import {webMessage} from '@xc/hiya-jsbridge'
import {amountToChips} from '~/pages/game/HorseGame/images/chips'
import {HorseConstIds} from './utils'
import {
  history_result_item,
  seat_list_item,
  StatusEnum,
  win_list_item,
  bet_status_enum,
  bet_detail_item,
  seat_type_enum,
} from './interface'

export const hr_homepage = createAsyncThunk(
  'hr_homepage',
  async (arg: {room_id?: number}, store: any) => {
    const {deviceinfo} = store.getState().env
    const resp = await http.post<
      HiyaRes<
        Pick<
          InitialStateType,
          | 'history_results'
          | 'round_no'
          | 'round_status'
          | 'status_expire'
          | 'horse_list'
          | 'seat_list'
          | 'my_icon'
          | 'my_nick_name'
          | 'my_balance'
          | 'my_win'
          | 'bet_chips'
          | 'repeat_enable'
          | 'horses_race_offsets'
          | 'stage_durations'
          | 'greedy_min_bet'
        >
      >
    >(
      '/live/game/horse_race_homepage',
      {
        room_id: deviceinfo.h_web_room_id,
        ...deviceinfo,
        ...arg,
      },
      {
        xcErrorToast: true,
      }
    )
    return resp.data.data
  }
)

export const hr_bet = createAsyncThunk(
  'hr_bet',
  async (
    arg: {
      bet_chip?: number
      bet_horse?: number
      repeated: boolean
      from: string
      room_id: number
    },
    store: any
  ) => {
    const resp = await http.post<
      HiyaRes<
        Pick<
          InitialStateType,
          'err_msg' | 'status' | 'bet_info' | 'balance'
        > & {
          jackpot_reward: number
        }
      >
    >(
      '/live/game/horse_race_bet',
      {
        ...store.getState().env.deviceinfo,
        ...arg,
      },
      {
        xcErrorToast: true,
      }
    )
    return resp.data.data
  }
)
export const hr_round_result = createAsyncThunk(
  'hr_round_result',
  async (
    arg: {
      round_no: string
    },
    store: any
  ) => {
    const resp = await http.post<
      HiyaRes<
        Pick<
          InitialStateType,
          | 'err_msg'
          | 'round_no'
          | 'win_list'
          | 'my_win'
          | 'my_bet'
          | 'my_balance'
          | 'win_horse'
        >
      >
    >('/live/game/hr_round_result', {
      ...store.getState().env.deviceinfo,
      ...arg,
    })
    return resp.data.data
  }
)
interface InitialStateType {
  // 赛马控制
  horse_win?: number
  round_status?: StatusEnum
  status_expire?: number
  horse_offsets?: any
  // 赛马信息
  horse_list?: any

  // 坐席相关
  pre_regal_name?: string // 富豪位变化前的名称
  pre_regal_icon?: string // 富豪位变化前的头像

  err_msg?: string
  round_no?: string
  // 获取轮次的中奖结果
  win_list?: win_list_item[]
  win_horse?: number
  my_bet?: number
  my_win?: number
  my_balance?: number

  chipType?: number
  // 页面是否在加载中
  init: boolean
  newUserGuide: boolean
  newUserGuideStep: number
  // 音乐播放
  playing: boolean
  showWarBillAms: boolean
  // 是否收到长连接消息
  isWebMsgGet: boolean

  history_results?: history_result_item[]
  // 当前阶段倒计时

  // 坐席信息
  seat_list?: seat_list_item[]
  my_nick_name?: string
  my_icon?: string
  bet_chips?: number[]
  // 是否可以重复上次下注信息
  repeat_enable?: boolean
  horses_race_offsets?: any
  stage_durations?: number[]

  bet_info?: {
    bet_horse: number
    bet_chips: bet_detail_item[]
    bet_sum: number
  }[]
  balance?: number
  // 下注结果状态:
  // 0:下注失败;1: 下注成功;2:余额不足
  status?: bet_status_enum
  greedy_min_bet?: {
    price: number
    countdown: number
  }
  // 前置流程完成标记
  hasPreLoadedAct: boolean
}

const initialState: InitialStateType = {
  init: false,
  newUserGuide: true,
  newUserGuideStep: 0,
  playing: false,
  showWarBillAms: false,
  isWebMsgGet: false,
  // 预渲染dom
  hasPreLoadedAct: false,
}

const raceHorseControl = (
  state,
  action: PayloadAction<
    Pick<
      InitialStateType,
      'win_horse' | 'horse_offsets' | 'status_expire' | 'round_status'
    >
  >
) => {
  Object.assign(state, {
    ...action.payload,
  })
}
export const HorseRaceSlice = createSlice({
  name: 'HorseGame',
  initialState,
  reducers: {
    // 前置流程完成标记
    finishedPreLoadedAct: (state) => {
      Object.assign(state, {
        hasPreLoadedAct: true,
      })
    },
    raceHorseControl,
    phaserInit: (state, action) => {
      Object.assign(state, {
        init: true,
      })
    },
    setWebMsgGet: (state, action) => {
      const {isWebMsgGet} = action.payload
      Object.assign(state, {
        isWebMsgGet,
      })
    },
    controlPlaying: (state, action) => {
      const {playing} = action.payload
      Object.assign(state, {
        playing,
      })
    },
    changeBetChip: (state, action: PayloadAction<{chipType: number}>) => {
      const {chipType} = action.payload
      Object.assign(state, {
        chipType,
      })
    },
    changeNewUserGuide: (
      state,
      action: PayloadAction<{
        newUserGuideStep?: number
        newUserGuide?: boolean
        newUserGuideVisiblity?: string
      }>
    ) => {
      Object.assign(state, action.payload)
    },
    controlWaybill: (
      state,
      action: PayloadAction<{showWarBillAms: boolean}>
    ) => {
      const {showWarBillAms} = action.payload
      Object.assign(state, {
        showWarBillAms,
      })
    },
    addHistoryRet: (
      state,
      action: PayloadAction<{win_pos: number; round_no: string}>
    ) => {
      const {history_results = []} = state
      const {win_pos, round_no} = action.payload
      history_results.unshift({
        win_pos,
        round_no,
      })
      Object.assign(state, {
        history_results,
      })
    },
    // 回收筹码之后置空
    clearBetNum: (state, action) => {
      const {horse_list} = state
      horse_list?.forEach((p) => {
        // eslint-disable-next-line no-param-reassign
        p.my_bets = 0
        // eslint-disable-next-line no-param-reassign
        p.bet_coins = 0
      })
      Object.assign(state, {
        horse_list,
      })
    },
    updateRichManMid: (
      state,
      action: PayloadAction<{
        new_regal_mid: number
      }>
    ) => {
      const {new_regal_mid} = action.payload
      const {seat_list} = state
      const regalIdx = seat_list.findIndex(
        (s) => s.seat_type === seat_type_enum.richMan
      )
      if (regalIdx < 0) {
        return
      }
      seat_list[regalIdx].player_mid = new_regal_mid
      Object.assign(state, {
        seat_list,
      })
    },
    updateRichMan: (
      state,
      action: PayloadAction<{
        new_regal_icon: string
        new_regal_name: string
      }>
    ) => {
      const {new_regal_icon, new_regal_name} = action.payload
      const {seat_list} = state
      const regalIdx = seat_list.findIndex(
        (s) => s.seat_type === seat_type_enum.richMan
      )
      if (regalIdx < 0) {
        return
      }
      const pre_regal_icon = seat_list[regalIdx].player_icon
      const pre_regal_name = seat_list[regalIdx].player_name

      seat_list[regalIdx].player_name = new_regal_name
      seat_list[regalIdx].player_icon = new_regal_icon

      Object.assign(state, {
        seat_list,
        pre_regal_icon,
        pre_regal_name,
      })
    },
    clearPreRegal: (state, action) => {
      Object.assign(state, {
        pre_regal_icon: '',
        pre_regal_name: '',
      })
    },
    updateBets: (state, action) => {
      const {horse_list} = state
      const {bet_details} = action.payload
      bet_details?.forEach((b, bIdx) => {
        const pIdx = horse_list?.findIndex((p) => p.horse_id === b.bet_pos)
        if (pIdx >= 0) {
          b.chip_list?.forEach((chip) => {
            horse_list[pIdx].bet_coins += chip.chip_num * chip.chip_value
          })
        }
      })
      Object.assign(state, {horse_list})
    },
  },
  extraReducers: (builder) => {
    // 主页
    builder.addCase(hr_homepage.fulfilled, (state, action) => {
      // 如果是结算阶段轮询的请求就不进行数据更新
      const {
        round_status,
        status_expire,
        horses_race_offsets,
        horse_list,
        history_results,
        round_no,
        seat_list,
        my_icon,
        my_balance,
        bet_chips,
        repeat_enable,
        my_win,
        stage_durations,
        greedy_min_bet,
      } = action.payload
      raceHorseControl(state, {
        payload: {
          round_status,
          status_expire,
          horse_offsets: horses_race_offsets,
          win_horse: horse_list?.find((v) => v.is_win)?.horse_id || 0,
        },
      } as any)
      if (greedy_min_bet) {
        bet_chips.unshift(greedy_min_bet.price)
      }
      // 提前出线了结果 且 不是结果阶段 或者 结果阶段没有完事
      if (
        history_results?.[0]?.round_no === round_no &&
        (round_status !== StatusEnum.showRes ||
          (round_status === StatusEnum.showRes && status_expire > 2))
      ) {
        // 最新的记录仅通过动画添加
        history_results?.shift()
      }
      Object.assign(state, {
        horse_list,
        history_results,
        round_no,
        seat_list,
        my_icon,
        my_balance,
        bet_chips,
        repeat_enable,
        my_win,
        stage_durations,
        greedy_min_bet,
      })
    })

    builder.addCase(hr_bet.fulfilled, (state, action) => {
      const {balance, bet_info, status} = action.payload
      if (status !== bet_status_enum.suc) {
        return
      }
      const {horse_list} = state
      bet_info?.forEach((b, bIdx) => {
        const pIdx = horse_list?.findIndex((p) => p.horse_id === b.bet_horse)
        b.bet_chips.forEach((chip) => {
          horse_list[pIdx].my_bets += chip.chip_num * chip.chip_value
          horse_list[pIdx].bet_coins += chip.chip_num * chip.chip_value
        })
      })
      Object.assign(state, {my_balance: balance, horse_list})
    })
  },
})

export default HorseRaceSlice.reducer
export const HorseRaceActions = HorseRaceSlice.actions

export const useMySeat = () => {
  const {seat_list, h_m} = useSelector((store) => {
    const {
      env: {deviceinfo},
      Animal_Falcon,
    } = store
    return {
      seat_list: Animal_Falcon.seat_list,
      h_m: deviceinfo.h_m,
    }
  })
  return useMemo(() => {
    const mySeat = (seat_list || []).find((v) => {
      return v.player_mid === h_m
    })
    return mySeat ? mySeat.seat_id : 0
  }, [seat_list])
}
export const getMySeat = (id): HTMLDivElement => {
  return document.querySelector(`#seat-${id}`)
}

const computationTime = () => {
  let s = +new Date()
  return () => {
    const newS = +new Date()
    const ret = (newS - s) / 1000
    s = newS
    return ret
  }
}
export const useMessage = (GameScene) => {
  const dispatch = useDispatch()
  const {h_m, ...HorseGame} = useSelector((store) => {
    return {
      h_m: store.env.deviceinfo.h_m,
      ...store.Animal_Falcon,
    }
  })
  let c
  const HorseGameWebMessageFn = useMemoizedFn(async (data) => {
    if (data && data.sub_type) {
      dispatch(HorseRaceActions.setWebMsgGet({isWebMsgGet: true}))
      setTimeout(() => {
        dispatch(HorseRaceActions.setWebMsgGet({isWebMsgGet: false}))
      }, 3000)
      if (Number(data.sub_type) === 209) {
        if (!c) {
          c = computationTime()
        }
        const temp: any = {
          horse_win: data.data.horse_win,
          round_status: data.data.new_status,
          status_expire: data.data.status_expire,
        }
        if (data.data.horse_offsets) {
          temp.horse_offsets = data.data.horse_offsets
        }
        dispatch(HorseRaceActions.raceHorseControl(temp))
      }
      if (Number(data.sub_type) === 210) {
        const {
          users_bet_details,
          new_regal_mid,
          new_regal_icon,
          new_regal_name,
        } = data.data
        if (new_regal_icon) {
          dispatch(
            HorseRaceActions.updateRichMan({
              new_regal_icon,
              new_regal_name,
            })
          )
          // 更新富豪mid要在头像更新之后 防止出筹码的地方对不上
          dispatch(HorseRaceActions.clearPreRegal({}))
          dispatch(HorseRaceActions.updateRichManMid({new_regal_mid}))
        }
        users_bet_details.forEach((item) => {
          const {bet_details, bet_mid} = item
          if (bet_mid !== h_m && bet_details?.length > 0) {
            // 判断当前下注的人是否在桌上
            const betPersonOnSeat = HorseGame.seat_list?.find(
              (s) => s.player_mid === bet_mid
            )
            const allChips = []
            dispatch(HorseRaceActions.updateBets({bet_details}))
            bet_details?.forEach((info) => {
              let amount = 0
              ;(info.chip_list || []).forEach((b) => {
                amount += b.chip_num * b.chip_value
              })
              const chips =
                HorseGame?.bet_chips?.length > 0
                  ? amountToChips(amount, [...HorseGame.bet_chips])
                  : []
              chips.forEach((chipKey) => {
                allChips.push({
                  key: info.bet_pos,
                  chipKey,
                })
              })
            })
            // 获取下注人的坐席位置
            const bet_seat = betPersonOnSeat ? betPersonOnSeat.seat_id : 2
            const startRef = getMySeat(bet_seat)
            allChips.forEach((v) => {
              GameScene?.phaserChip?.addChipAnimation({
                isMy: false,
                startRef,
                ...v,
              })
            })
          }
        })
      }
    }
  })
  useEffect(() => {
    // 在引导期间直接返回
    if (HorseGame.newUserGuide) {
      return
    }
    webMessage(HorseGameWebMessageFn)
  }, [HorseGame.newUserGuide])
}
